import React from 'react';
import { DownOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, ButtonVariants, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import SectionCard from '../sectionCard/SectionCard';
import EmptyState from '../emptyState/EmptyState';
import PayrollStatusPill from '../payrollStatusPill/PayrollStatusPill';
import { formatRelativeAge } from '../../../utils/datetime';
import PayrollConnectionHistory from './PayrollConnectionHistory';
import { UNASSIGNED_CONNECTION_KEY, getConnectionPlatformLabel } from './payrollConnections.constants';
import styles from './styles/payroll-connections.module.scss';

/**
 * One collapsible card per payroll connection a member has.
 *
 * Expansion is *controlled*: the panel renders what `expandedKeys` says and
 * reports every toggle through `onToggleConnection`. It is not local UI state,
 * because what is open decides where the page puts content that must stay
 * reachable — the User page moves its sync card (and the two admin actions on
 * it) out of the rail and into a panel only while that panel is open, and it
 * can only make that call if it knows. The events themselves, and the
 * fetch-once guard, stay in the controller for the same reason they always
 * did: expand → collapse → re-expand costs no request, while a *failed* first
 * load stays retryable.
 *
 * A member with two jobs gets two cards, each with its own platform, state and
 * history, so a repeatedly-breaking connection can never be mistaken for two
 * different ones.
 *
 * @param {Object} props
 * @param {Array<Object> | null} [props.connections]
 * @param {string} [props.timezone]
 * @param {boolean} [props.loading]
 * @param {Object | null} [props.error]
 * @param {() => void} [props.onRetry]
 * @param {Object} [props.eventsByKey] `{ [connection_key]: { items, pageinfo, loading, loadingMore, error, moreError } }`
 * @param {Array<string>} [props.expandedKeys] Connection keys currently open.
 * @param {(connectionKey: string, expanded: boolean) => void} [props.onToggleConnection]
 * @param {(connectionKey: string) => void} [props.onLoadMoreEvents]
 * @param {(connectionKey: string) => void} [props.onRetryEvents]
 * @param {(connectionKey: string) => React.ReactNode} [props.renderConnectionDetail]
 * @param {boolean} [props.summaryOnly] Headers only — platform, state, last
 *   connection, event count — with no expand control and no history. The mode
 *   KAN-499 mounts inside the Reports user panel.
 *   Extra detail rendered inside the expanded panel it belongs to — the User
 *   page passes the per-dimension sync card.
 */
function PayrollConnectionsPanel({
    connections,
    timezone,
    loading = false,
    error = null,
    onRetry,
    eventsByKey,
    expandedKeys,
    onToggleConnection,
    onLoadMoreEvents,
    onRetryEvents,
    renderConnectionDetail,
    summaryOnly = false,
}) {
    const hasConnections = Array.isArray(connections) && connections.length > 0;

    // A failed read only owns the whole surface when there is nothing behind
    // it. `reloadPayrollConnections` keeps `data` on failure, so once the
    // summary has loaded once the last good list stays on screen and the
    // failure is reported over it — an admin who just marked a sync complete
    // would otherwise watch their connections vanish because the *refresh*
    // failed, not the connections.
    if (error && !hasConnections) {
        return (
            <SectionCard icon={<LinkOutlined />} title="Payroll Connections">
                <div className={styles.historyState}>
                    <EmptyState
                        title="Couldn't load payroll connections"
                        description="The connection record could not be read. Please try again."
                    />
                    {onRetry ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            className={styles.retryButton}
                            onClick={() => onRetry()}
                        >
                            Retry
                        </Button>
                    ) : null}
                </div>
            </SectionCard>
        );
    }

    if (loading && !connections) {
        return (
            <SectionCard icon={<LinkOutlined />} title="Payroll Connections">
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.historyNote}>
                    Loading payroll connections…
                </Text>
            </SectionCard>
        );
    }

    if (!hasConnections) {
        return (
            <SectionCard icon={<LinkOutlined />} title="Payroll Connections">
                <EmptyState
                    title="No payroll connections"
                    description="This member has never started a payroll connection."
                />
            </SectionCard>
        );
    }

    return (
        <div className={styles.panelStack}>
            {error ? (
                <div className={`${styles.panelNotice} ${styles.panelNoticeError}`} role="alert">
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.panelNoticeErrorText}>
                        Couldn't refresh payroll connections. Showing the last loaded record.
                    </Text>
                    {onRetry ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            className={styles.panelNoticeButton}
                            onClick={() => onRetry()}
                        >
                            Retry
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {/* A re-read in progress over a list that is already on screen. The
                rows stay readable, but they are not silently presented as
                current while a newer answer is in flight. */}
            {loading && !error ? (
                <div className={styles.panelNotice} role="status">
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.panelNoticeText}>
                        Refreshing payroll connections…
                    </Text>
                </div>
            ) : null}

            {connections.map((connection) => {
                const key = connection?.connection_key ?? UNASSIGNED_CONNECTION_KEY;
                return (
                    <PayrollConnectionCard
                        key={key}
                        connection={connection}
                        connectionKey={key}
                        timezone={timezone}
                        events={eventsByKey?.[key]}
                        expanded={Array.isArray(expandedKeys) && expandedKeys.includes(key)}
                        onToggleConnection={onToggleConnection}
                        onLoadMoreEvents={onLoadMoreEvents}
                        onRetryEvents={onRetryEvents}
                        renderConnectionDetail={renderConnectionDetail}
                        summaryOnly={summaryOnly}
                    />
                );
            })}
        </div>
    );
}

/**
 * A single connection's card. Header is always readable without expanding:
 * platform, state, when it last connected, when it was last tried, how many
 * events it has.
 */
function PayrollConnectionCard({
    connection,
    connectionKey,
    timezone,
    events,
    expanded,
    onToggleConnection,
    onLoadMoreEvents,
    onRetryEvents,
    renderConnectionDetail,
    summaryOnly = false,
}) {
    const platformLabel = getConnectionPlatformLabel(connection);
    const eventsCount = Number(connection?.events_count) || 0;

    const lastConnected = connection?.last_successful_at
        ? `Last connected ${formatRelativeAge(connection.last_successful_at)}`
        : 'Never connected';

    // The attempt is the fact "Never connected" leaves out: a job that broke an
    // hour ago otherwise reads exactly like one nobody ever tried. Dropped when
    // the last attempt *is* the last success, where it would only restate the
    // line beside it.
    const lastAttempt =
        connection?.last_attempt_at && connection.last_attempt_at !== connection.last_successful_at
            ? `Last attempt ${formatRelativeAge(connection.last_attempt_at)}`
            : null;

    const detail = typeof renderConnectionDetail === 'function' ? renderConnectionDetail(connectionKey) : null;

    return (
        // A labelled region per connection: it gives screen readers a name to
        // announce when the panel expands, and keeps each connection's facts
        // addressable as a group rather than as loose text on the page.
        <section className={styles.connectionRegion} aria-label={platformLabel}>
            <SectionCard
                icon={<LinkOutlined />}
                title={platformLabel}
                action={
                    <div className={styles.headerActions}>
                        <PayrollStatusPill state={connection?.state} />
                        {!summaryOnly ? (
                            <Button
                                variant={ButtonVariants.TEXT}
                                className={styles.collapseButton}
                                aria-label={expanded ? `Collapse ${platformLabel}` : `Expand ${platformLabel}`}
                                aria-expanded={!!expanded}
                                onClick={() => onToggleConnection?.(connectionKey, !expanded)}
                            >
                                <DownOutlined className={expanded ? styles.chevron : styles.chevronCollapsed} />
                            </Button>
                        ) : null}
                    </div>
                }
            >
                <div className={styles.headerMeta}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.headerMetaPrimary}>
                        {lastConnected}
                    </Text>
                    {lastAttempt ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.headerMetaSecondary}>
                            {lastAttempt}
                        </Text>
                    ) : null}
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.headerMetaSecondary}>
                        {eventsCount === 1 ? '1 event' : `${eventsCount} events`}
                    </Text>
                </div>

                {expanded && !summaryOnly ? (
                    <div className={styles.cardBody}>
                        {detail}
                        <PayrollConnectionHistory
                            items={events?.items}
                            pageinfo={events?.pageinfo}
                            loading={!!events?.loading}
                            loadingMore={!!events?.loadingMore}
                            error={events?.error || null}
                            moreError={events?.moreError || null}
                            timezone={timezone}
                            connectionLabel={platformLabel}
                            onRetry={() => onRetryEvents?.(connectionKey)}
                            onLoadMore={() => onLoadMoreEvents?.(connectionKey)}
                        />
                    </div>
                ) : null}
            </SectionCard>
        </section>
    );
}

export default PayrollConnectionsPanel;
